import { useCallback, useEffect, useState } from 'react'
import { Defaults } from '../../modules/API/Defaults/defaults'

const SKIPPED_KEY = 'skipped-app-version'

type VersionCheckState = 'idle' | 'up-to-date' | 'mismatch-prompt' | 'mismatch-skipped'

interface UseAppVersionCheckResult {
    state: VersionCheckState
    remoteVersion: string | null
    skip: () => void
    update: () => void
}

export const useAppVersionCheck = (): UseAppVersionCheckResult => {
    const [state, setState] = useState<VersionCheckState>('idle')
    const [remoteVersion, setRemoteVersion] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            try {
                const res = await Defaults.getAppVersion()
                if (cancelled) return
                const v = res?.v
                if (!v) return
                setRemoteVersion(v)
                if (v === __APP_VERSION__) {
                    setState('up-to-date')
                    return
                }
                const skipped = localStorage.getItem(SKIPPED_KEY)
                setState(skipped === v ? 'mismatch-skipped' : 'mismatch-prompt')
            } catch {
                // Backend unreachable — stay idle, never block UI on version check.
            }
        }
        run()
        return () => {
            cancelled = true
        }
    }, [])

    const skip = useCallback(() => {
        if (!remoteVersion) return
        localStorage.setItem(SKIPPED_KEY, remoteVersion)
        setState('mismatch-skipped')
    }, [remoteVersion])

    const update = useCallback(() => {
        localStorage.removeItem(SKIPPED_KEY)
        window.location.reload()
    }, [])

    return { state, remoteVersion, skip, update }
}
